import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiUrl';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  FiGift, FiSearch, FiEye, FiMail, FiCheck, FiX, FiClock,
  FiUser, FiCalendar, FiDollarSign, FiCopy, FiDownload,
  FiRefreshCw, FiPlus
} from 'react-icons/fi';
import { toast } from 'sonner';
import api from '../../lib/api';

const API_URL = getApiUrl();

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: FiClock },
  paid: { label: 'Payée', color: 'bg-blue-100 text-blue-700', icon: FiCheck },
  sent: { label: 'Envoyée', color: 'bg-purple-100 text-purple-700', icon: FiMail },
  used: { label: 'Utilisée', color: 'bg-green-100 text-green-700', icon: FiCheck },
  expired: { label: 'Expirée', color: 'bg-red-100 text-red-700', icon: FiX },
  cancelled: { label: 'Annulée', color: 'bg-slate-100 text-slate-700', icon: FiX }
};

const prestationNames = {
  photobooth: 'Photobooth',
  videobooth: 'Vidéobooth 360°',
  pack: 'Pack Photobooth + Vidéobooth'
};

export default function GiftCardsPage() {
  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Formulaire de création
  const [newCard, setNewCard] = useState({
    sender_first_name: '',
    sender_last_name: '',
    sender_email: '',
    sender_phone: '',
    recipient_name: '',
    recipient_email: '',
    prestation: 'photobooth',
    amount: 350,
    message: ''
  });

  useEffect(() => {
    loadGiftCards();
  }, []);

  const loadGiftCards = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gift-cards`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGiftCards(data.gift_cards || []);
      }
    } catch (error) {
      console.error('Error loading gift cards:', error);
      toast.error('Erreur lors du chargement des cartes cadeaux');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (cardId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gift-cards/${cardId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success('Statut mis à jour');
        loadGiftCards();
        setDetailOpen(false);
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copié !');
  };

  const handleCreateCard = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gift-cards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCard)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Carte cadeau créée ! Code: ${data.code}`);
        setCreateOpen(false);
        setNewCard({
          sender_first_name: '',
          sender_last_name: '',
          sender_email: '',
          sender_phone: '',
          recipient_name: '',
          recipient_email: '',
          prestation: 'photobooth',
          amount: 350,
          message: ''
        });
        loadGiftCards();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const filteredCards = giftCards.filter(card => {
    const matchesSearch = 
      card.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      card.recipient_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      card.sender_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || card.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: giftCards.length,
    pending: giftCards.filter(c => c.status === 'pending').length,
    paid: giftCards.filter(c => c.status === 'paid').length,
    used: giftCards.filter(c => c.status === 'used').length,
    totalValue: giftCards.reduce((sum, c) => sum + (c.amount || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FiRefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FiGift className="h-6 w-6 text-indigo-600" />
            Cartes Cadeaux
          </h1>
          <p className="text-slate-500 mt-1">Gérez et suivez vos cartes cadeaux</p>
        </div>
        <Button 
          onClick={() => setCreateOpen(true)}
          className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
        >
          <FiPlus className="mr-2 h-4 w-4" />
          Créer une carte cadeau
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="text-sm text-slate-500">Total</div>
          <div className="text-2xl font-bold">{stats.total}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-slate-500">En attente</div>
          <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-slate-500">Payées</div>
          <div className="text-2xl font-bold text-blue-600">{stats.paid}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-slate-500">Utilisées</div>
          <div className="text-2xl font-bold text-green-600">{stats.used}</div>
        </Card>
        <Card className="p-4">
          <div className="text-sm text-slate-500">Valeur totale</div>
          <div className="text-2xl font-bold text-indigo-600">{stats.totalValue}€</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher par code, nom..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'pending', 'paid', 'sent', 'used', 'expired'].map(status => (
              <Button
                key={status}
                variant={filterStatus === status ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus(status)}
              >
                {status === 'all' ? 'Tous' : statusConfig[status]?.label}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      {/* Gift Cards List */}
      {filteredCards.length === 0 ? (
        <Card className="p-12 text-center">
          <FiGift className="h-12 w-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-600">Aucune carte cadeau</h3>
          <p className="text-slate-400 mt-2">Les cartes cadeaux commandées apparaîtront ici</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredCards.map((card) => {
            const StatusIcon = statusConfig[card.status]?.icon || FiClock;
            return (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setSelectedCard(card); setDetailOpen(true); }}>
                  <div className="flex items-center gap-4">
                    {/* Icon */}
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white">
                      <FiGift className="h-6 w-6" />
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-lg">{card.code}</span>
                        <button onClick={(e) => { e.stopPropagation(); copyCode(card.code); }} className="p-1 hover:bg-slate-100 rounded">
                          <FiCopy className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="text-sm text-slate-500 flex items-center gap-3 mt-1">
                        <span className="flex items-center gap-1">
                          <FiUser className="h-3 w-3" />
                          Pour: {card.recipient_name}
                        </span>
                        <span>•</span>
                        <span>De: {card.sender_name}</span>
                      </div>
                    </div>
                    
                    {/* Prestation & Amount */}
                    <div className="text-right">
                      <div className="font-bold text-lg">{card.amount}€</div>
                      <div className="text-sm text-slate-500">{prestationNames[card.prestation] || card.prestation}</div>
                    </div>
                    
                    {/* Status */}
                    <Badge className={statusConfig[card.status]?.color}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusConfig[card.status]?.label}
                    </Badge>
                    
                    {/* Action */}
                    <Button variant="ghost" size="sm">
                      <FiEye className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FiGift className="h-5 w-5 text-amber-500" />
              Carte Cadeau - {selectedCard?.code}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCard && (
            <div className="space-y-6 py-4">
              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <div className="text-sm text-slate-500">Statut actuel</div>
                  <Badge className={`mt-1 ${statusConfig[selectedCard.status]?.color}`}>
                    {statusConfig[selectedCard.status]?.label}
                  </Badge>
                </div>
                <div className="text-right">
                  <div className="text-sm text-slate-500">Montant</div>
                  <div className="text-2xl font-bold text-indigo-600">{selectedCard.amount}€</div>
                </div>
              </div>
              
              {/* Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Destinataire</Label>
                  <p className="font-medium">{selectedCard.recipient_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Expéditeur</Label>
                  <p className="font-medium">{selectedCard.sender_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email expéditeur</Label>
                  <p className="text-sm">{selectedCard.sender_email}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Téléphone</Label>
                  <p className="text-sm">{selectedCard.sender_phone}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-500">Prestation</Label>
                  <p className="font-medium">{prestationNames[selectedCard.prestation] || selectedCard.prestation}</p>
                </div>
              </div>
              
              {/* Message */}
              {selectedCard.message && (
                <div>
                  <Label className="text-xs text-slate-500">Message personnalisé</Label>
                  <div className="mt-1 p-3 bg-amber-50 rounded-lg border border-amber-100 italic">
                    "{selectedCard.message}"
                  </div>
                </div>
              )}
              
              {/* Dates */}
              <div className="flex justify-between text-sm text-slate-500 pt-4 border-t">
                <span>Créée le: {new Date(selectedCard.created_at).toLocaleDateString('fr-FR')}</span>
                <span>Valide jusqu'au: {new Date(selectedCard.valid_until).toLocaleDateString('fr-FR')}</span>
              </div>
              
              {/* Actions */}
              <div className="flex flex-col gap-3 pt-4">
                {selectedCard.status === 'pending' && (
                  <>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800 font-medium">💳 Envoyez un lien de paiement au client</p>
                    </div>
                    <Button 
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API_URL}/api/payments/mypos/gift-card/${selectedCard.id}/payment-link`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${localStorage.getItem('token')}`,
                              'Content-Type': 'application/json'
                            }
                          });
                          const data = await response.json();
                          if (data.success && data.payment_link_url) {
                            await navigator.clipboard.writeText(data.payment_link_url);
                            toast.success(`Lien de paiement (${data.amount}€) copié ! Envoyez-le au client.`);
                            loadGiftCards();
                          } else {
                            toast.error(data.detail || 'Erreur création lien de paiement');
                          }
                        } catch (error) {
                          toast.error('Erreur: ' + error.message);
                        }
                      }} 
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="mypos-giftcard-payment-btn"
                    >
                      <FiDollarSign className="mr-2 h-4 w-4" /> Créer lien de paiement MyPOS
                    </Button>
                    <Button onClick={() => updateStatus(selectedCard.id, 'paid')} variant="outline" className="w-full">
                      <FiCheck className="mr-2 h-4 w-4" /> Marquer comme payée (manuellement)
                    </Button>
                  </>
                )}
                {selectedCard.status === 'paid' && (
                  <div className="flex gap-2">
                    <Button onClick={() => updateStatus(selectedCard.id, 'sent')} className="flex-1 bg-purple-600 hover:bg-purple-700">
                      <FiMail className="mr-2 h-4 w-4" /> Marquer comme envoyée
                    </Button>
                    <Button variant="outline" className="flex-1">
                      <FiDownload className="mr-2 h-4 w-4" /> Télécharger PDF
                    </Button>
                  </div>
                )}
                {(selectedCard.status === 'paid' || selectedCard.status === 'sent') && (
                  <Button onClick={() => updateStatus(selectedCard.id, 'used')} variant="outline" className="w-full">
                    <FiCheck className="mr-2 h-4 w-4" /> Marquer comme utilisée
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Card Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FiGift className="h-5 w-5 text-amber-500" />
              Créer une carte cadeau
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateCard} className="space-y-6 py-4">
            {/* Expéditeur */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900 flex items-center gap-2">
                <FiUser className="h-4 w-4" /> Informations de l'expéditeur
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sender_first_name">Prénom *</Label>
                  <Input
                    id="sender_first_name"
                    value={newCard.sender_first_name}
                    onChange={(e) => setNewCard({ ...newCard, sender_first_name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sender_last_name">Nom *</Label>
                  <Input
                    id="sender_last_name"
                    value={newCard.sender_last_name}
                    onChange={(e) => setNewCard({ ...newCard, sender_last_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sender_email">Email *</Label>
                  <Input
                    id="sender_email"
                    type="email"
                    value={newCard.sender_email}
                    onChange={(e) => setNewCard({ ...newCard, sender_email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sender_phone">Téléphone *</Label>
                  <Input
                    id="sender_phone"
                    type="tel"
                    value={newCard.sender_phone}
                    onChange={(e) => setNewCard({ ...newCard, sender_phone: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Destinataire */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900 flex items-center gap-2">
                <FiGift className="h-4 w-4" /> Informations du destinataire
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="recipient_name">Nom du destinataire *</Label>
                  <Input
                    id="recipient_name"
                    value={newCard.recipient_name}
                    onChange={(e) => setNewCard({ ...newCard, recipient_name: e.target.value })}
                    placeholder="Ex: Marie & Jean"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="recipient_email">Email du destinataire</Label>
                  <Input
                    id="recipient_email"
                    type="email"
                    value={newCard.recipient_email}
                    onChange={(e) => setNewCard({ ...newCard, recipient_email: e.target.value })}
                    placeholder="Optionnel"
                  />
                </div>
              </div>
            </div>

            {/* Prestation & Montant */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900 flex items-center gap-2">
                <FiDollarSign className="h-4 w-4" /> Détails du cadeau
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="prestation">Prestation *</Label>
                  <Select
                    value={newCard.prestation}
                    onValueChange={(value) => setNewCard({ ...newCard, prestation: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="photobooth">Photobooth</SelectItem>
                      <SelectItem value="videobooth">Vidéobooth 360°</SelectItem>
                      <SelectItem value="pack">Pack Photobooth + Vidéobooth</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount">Montant (€) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    min="50"
                    step="10"
                    value={newCard.amount}
                    onChange={(e) => setNewCard({ ...newCard, amount: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message">Message personnalisé</Label>
              <Textarea
                id="message"
                value={newCard.message}
                onChange={(e) => setNewCard({ ...newCard, message: e.target.value })}
                placeholder="Écrivez un message personnalisé pour le destinataire..."
                rows={3}
              />
            </div>

            <DialogFooter className="pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                disabled={creating}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                {creating ? (
                  <>
                    <FiRefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Création...
                  </>
                ) : (
                  <>
                    <FiGift className="mr-2 h-4 w-4" />
                    Créer la carte cadeau
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
